/** @graphql cc **/
import { graphql } from 'react-relay';
import { useMemo } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useContentTitlesQuery$data,
	useContentTitlesQuery$variables,
} from './__generated__/useContentTitlesQuery.graphql.ts';

const ContentTitlesQuery = graphql`
	query useContentTitlesQuery($ids: [ID!]!) {
		content(ids: $ids) {
			nodes {
				id
				title
			}
		}
	}
`;

export type UseContentTitlesResult = {
	titlesByContentId: Map<string, string>;
	isLoading: boolean;
};

/**
 * Fetches content titles for a list of content IDs in a single batch query.
 * Returns a Map of contentId -> title. IDs with no result or missing title map to empty string.
 */
export const useContentTitles = (contentIds: string[]): UseContentTitlesResult => {
	const ids = useMemo(() => {
		const unique = [...new Set(contentIds)].filter(Boolean) as string[];
		return unique;
	}, [contentIds]);

	const { data, loading } = useQueryInterop<
		useContentTitlesQuery$data,
		useContentTitlesQuery$variables
	>(ContentTitlesQuery, {
		variables: { ids },
		skip: ids.length === 0,
	});

	const titlesByContentId = useMemo(() => {
		const map = new Map<string, string>();
		const nodes = data?.content?.nodes ?? [];
		for (const node of nodes) {
			if (node?.id != null) {
				map.set(node.id, node.title ?? '');
			}
		}
		return map;
	}, [data]);

	const isLoading = ids.length > 0 && loading;

	return { titlesByContentId, isLoading };
};
